import { cloneDeep, findIndex, isEqual, isNil } from "lodash-es";
import { computed, type Ref, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import ModalService from "shared/modals/ModalService";
import { type ExternalItem } from "shared/resources";
import StreamService from "shared/services/api/StreamService";
import { saveExternalItems } from "shared/services/externalItem";
import { type Maybe, type Nullable } from "shared/types";
import { MentionType } from "shared/types/mentions";

import { type ExternalItemManualAddModalProps } from "../ExternalItemManualAddModal.vue";
import { urlAlreadyAdded, urlCanBeAdded } from "../shared/helpers";
import {
  type ExistingMentionData,
  type UrlData,
  type UrlValueChange,
} from "../shared/types";

import useUrlsVerification from "./useUrlsVerification";
import { UrlStatus } from "./useUrlVerificationNotificationsSubscription";

const SELECT_ACTION_MODAL_NAME = "ExternalItemSelectActionModal";
const BULK_ADD_MODAL_NAME = "ExternalItemsBulkAddModal";
const MANUAL_ADD_MODAL_NAME = "ExternalItemManualAddModal";

export default function useAddExternalItemsWorkflow({
  streamId,
}: Pick<ExternalItemManualAddModalProps, "streamId">) {
  const verifiedUrlsData: Ref<Nullable<UrlData[]>> = ref(null);

  const externalItemsAddedInCurrentFlow: Ref<ExternalItem[]> = ref([]);

  const urlCurrentlyBeingAdded: Ref<Nullable<string>> = ref(null);

  const savingInProgress = ref<boolean>(false);

  let onBulkAddModalClose: Maybe<() => void>;

  const urlValueChange: Ref<Nullable<UrlValueChange>> = ref(null);

  const {
    cancelVerification,
    initializeUrlData,
    performVerification,
    preVerifyUrlsLocally,
    verificationInProgress,
  } = useUrlsVerification();

  function openSelectActionModal() {
    ModalService.open(SELECT_ACTION_MODAL_NAME, {
      events: {
        bulkAdd: openBulkAddModal,
        cancel: closeAllModals,
        manualAdd: openManualAddModal,
      },
    });
  }

  function openBulkAddModal() {
    onBulkAddModalClose = cancelVerification;

    ModalService.open(BULK_ADD_MODAL_NAME, {
      events: {
        addUrl: (url: string) => {
          urlCurrentlyBeingAdded.value = url;
          openManualAddModal();
        },
        cancel: closeAllModals,
        done: finalizeBulkAdding,
        goBack: closeBulkAddModal,
        modalClosed: closeAllModals,
        resetVerificationResults: () => {
          verifiedUrlsData.value = null;
        },
        urlValueChangeHandled: (urlChange: UrlValueChange) => {
          // reset when applied for event-like communication
          if (isEqual(urlChange, urlValueChange.value)) {
            urlValueChange.value = null;
          }
        },
        verifyUrls: verifyUrlsForBulkAdding,
      },
      props: {
        donePending: savingInProgress,
        streamId,
        urlValueChange,
        verificationInProgress,
        verifiedUrlsData,
      },
    });
  }

  function openManualAddModal() {
    const openedFromBulkAdd = computed(() =>
      Boolean(urlCurrentlyBeingAdded.value)
    );

    ModalService.open(MANUAL_ADD_MODAL_NAME, {
      events: {
        cancel: openedFromBulkAdd.value ? closeManualAddModal : closeAllModals,
        goBack: closeManualAddModal,
        modalClosed: closeAllModals,
        save: openedFromBulkAdd.value
          ? handleManualAddingOfUrlFromBulk
          : finalizeManualAdding,
      },
      props: {
        openedFromBulkAdd: openedFromBulkAdd.value,
        preselectedUrl: urlCurrentlyBeingAdded.value,
        savingInProgress: openedFromBulkAdd.value
          ? verificationInProgress
          : savingInProgress,
        streamId,
      },
    });
  }

  async function verifyUrlsForBulkAdding(urls: string[]) {
    verifiedUrlsData.value = urls.map(initializeUrlData);

    try {
      // this will update the urls' data one by one during execution
      await performVerification(verifiedUrlsData.value);
    } catch (error) {
      handleApiVerificationError(error);
      verifiedUrlsData.value = null;
    }
  }

  async function finalizeBulkAdding() {
    await saveWithProgressAndErrorsHandling("bulk_add", async () => {
      let justCreatedExternalItems: ExistingMentionData[] = [];

      if (externalItemsAddedInCurrentFlow.value.length > 0) {
        await saveExternalItems(externalItemsAddedInCurrentFlow.value);

        justCreatedExternalItems = externalItemsAddedInCurrentFlow.value.map(
          ({ id }) => ({
            id: Number(id),
            type: MentionType.external_item,
          })
        );
      }

      const existingMentions: ExistingMentionData[] = (
        verifiedUrlsData.value ?? []
      )
        .filter(
          (urlData: UrlData) =>
            urlAlreadyAdded(urlData) && !urlData.addedInCurrentFlow
        )
        .map((urlData) => ({
          id: urlData.mentionId!,
          type: urlData.mentionType!,
        }));

      const mentionsToBeAddedToStream = [
        ...existingMentions,
        ...justCreatedExternalItems,
      ];

      if (mentionsToBeAddedToStream.length > 0) {
        await StreamService.forceAddMentions(streamId, {
          mentions: mentionsToBeAddedToStream,
        });
      }

      closeAllModals();
    });
  }

  async function handleManualAddingOfUrlFromBulk(externalItem: ExternalItem) {
    if (isNil(verifiedUrlsData.value)) {
      throw new Error("Something went wrong - no verifiedUrlsData");
    }

    if (isNil(urlCurrentlyBeingAdded.value)) {
      throw new Error("Something went wrong - no urlCurrentlyBeingAdded");
    }

    const oldUrlValue = urlCurrentlyBeingAdded.value!;
    const newUrlValue = externalItem.url;

    const addedUrlDataIndex: number = findIndex(verifiedUrlsData.value, {
      url: oldUrlValue,
    });

    if (newUrlValue === oldUrlValue) {
      const addedUrlData: UrlData = verifiedUrlsData.value[addedUrlDataIndex];

      markAsAlreadyAdded(addedUrlData);
      externalItemsAddedInCurrentFlow.value.push(externalItem);
    } else {
      await handleUrlChange(
        newUrlValue,
        oldUrlValue,
        addedUrlDataIndex,
        externalItem
      );
    }

    closeManualAddModal();
  }

  async function handleUrlChange(
    newValue: string,
    oldValue: string,
    urlDataIndex: number,
    externalItem: ExternalItem
  ) {
    if (isNil(verifiedUrlsData.value)) {
      throw new Error("Something went wrong - no verifiedUrlsData");
    }

    const newUrlData = initializeUrlData(newValue);

    // work on a copy first to keep the component state consistent in case of an API error
    // (we need a deep copy due to in-place modifications done by the verification functions)
    const newVerifiedUrlsData = cloneDeep(verifiedUrlsData.value);

    newVerifiedUrlsData[urlDataIndex] = newUrlData;

    // re-check full list for duplicates
    preVerifyUrlsLocally(newVerifiedUrlsData);

    try {
      await performVerification([newUrlData], { preVerifyLocally: false });
    } catch (error) {
      handleApiVerificationError(error);

      return;
    }

    // there was no error - now we can actually apply all the changes
    verifiedUrlsData.value = newVerifiedUrlsData;

    if (urlCanBeAdded(newUrlData)) {
      markAsAlreadyAdded(newUrlData);
      externalItemsAddedInCurrentFlow.value.push(externalItem);
    }

    urlValueChange.value = { newValue, oldValue };
  }

  function markAsAlreadyAdded(urlData: UrlData) {
    Object.assign(urlData, {
      addedInCurrentFlow: true,
      mentionType: MentionType.external_item,
      status: UrlStatus.alreadyAdded,
    });
  }

  async function finalizeManualAdding(externalItem: ExternalItem) {
    await saveWithProgressAndErrorsHandling("manual_add", async () => {
      await saveExternalItems([externalItem]);
      closeAllModals();
    });
  }

  function resetState() {
    verifiedUrlsData.value = null;
    externalItemsAddedInCurrentFlow.value = [];
    urlCurrentlyBeingAdded.value = null;
    urlValueChange.value = null;
  }

  function closeSelectActionModal() {
    ModalService.close(SELECT_ACTION_MODAL_NAME);
  }

  function closeBulkAddModal() {
    onBulkAddModalClose?.();
    ModalService.close(BULK_ADD_MODAL_NAME);
    resetState();
  }

  function closeManualAddModal() {
    ModalService.close(MANUAL_ADD_MODAL_NAME);
    urlCurrentlyBeingAdded.value = null;
  }

  function closeAllModals() {
    // simply close all possible modals - ModalService will just ignore those that aren't opened
    closeSelectActionModal();
    closeBulkAddModal();
    closeManualAddModal();
  }

  async function saveWithProgressAndErrorsHandling(
    operation: "manual_add" | "bulk_add",
    doSave: () => Promise<any>
  ): Promise<void> {
    savingInProgress.value = true;

    try {
      await doSave();
    } catch (error) {
      ErrorDialog(
        getLocaleText(`external_items_add_workflow.saving_error.${operation}`),
        error
      );
    } finally {
      savingInProgress.value = false;
    }
  }

  function handleApiVerificationError(error: unknown) {
    ErrorDialog(
      getLocaleText("external_items_bulk_add_modal.verification_error"),
      error
    );
  }

  return {
    startAddExternalItemsWorkflow: openSelectActionModal,
  };
}
